import { useToast } from '@chakra-ui/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { HandleAtsCallbackRequest } from '@pkg/types/api'

import api from '@/api'

import { TEAM_KEY } from '../team'

const ATS_SYNC_STATS_KEY = 'ats-sync-stats'

export const useHandleAtsCallback = () => {
  const queryClient = useQueryClient()
  const toast = useToast()

  return useMutation({
    mutationFn: async (data: HandleAtsCallbackRequest) => {
      const response = await api.handleAtsCallback(data)
      return response.data
    },
    onSuccess: () => {
      // Invalidate team data to refresh ATS connection status
      queryClient.invalidateQueries({ queryKey: [TEAM_KEY] })
      toast({
        title: 'Success',
        description: 'ATS integration connected successfully',
        status: 'success',
        duration: 5000,
        isClosable: true,
      })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: 'Failed to connect ATS integration',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    },
  })
}

export const useDisconnectAtsIntegration = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const response = await api.disconnectAtsIntegration()
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEAM_KEY] })
    },
  })
}

export const useAtsSyncStats = (enabled: boolean) => {
  return useQuery({
    queryKey: [ATS_SYNC_STATS_KEY],
    queryFn: async () => {
      const response = await api.getAtsSyncStats()
      return response.data
    },
    enabled,
    staleTime: 30_000,
  })
}

export const useTriggerAtsUserSync = () => {
  const queryClient = useQueryClient()
  const toast = useToast()

  return useMutation({
    mutationFn: async () => {
      const response = await api.triggerAtsUserSync()
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEAM_KEY] })
      toast({
        title: 'Sync started',
        description: 'ATS user accounts are being synced in the background.',
        status: 'info',
        duration: 3000,
        isClosable: true,
      })
    },
    onError: () => {
      toast({
        title: 'Sync failed',
        description: 'Could not start user sync. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    },
  })
}
